import { ArrowUpDown, ThumbsUp, ThumbsDown, Activity, Users, Send, Star } from 'lucide-react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line,
} from 'recharts';
import type { TimelineBucket, AnalyticsSummary } from '../../types/analytics';

interface Props {
  summary: AnalyticsSummary;
  timeline: TimelineBucket[];
}

export default function VotingPatterns({ summary, timeline }: Props) {
  const upPct = summary.totalVotes > 0
    ? Math.round((summary.totalUpvotes / summary.totalVotes) * 100)
    : 50;

  // Voting velocity: votes per 15-min bucket
  const velocityData = timeline.map(b => ({
    time: b.bucket,
    votes: b.votes,
    upvotes: b.upvotes,
    downvotes: b.downvotes,
  }));

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Vote Distribution */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <ArrowUpDown className="w-5 h-5 text-primary" />
          Vote Distribution
        </h3>
        {summary.totalVotes === 0 ? (
          <div className="h-32 flex items-center justify-center text-gray-500">
            <p>No votes cast yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                  <ThumbsUp className="w-3.5 h-3.5" /> {summary.totalUpvotes} upvotes
                </span>
                <span className="text-red-500 dark:text-red-400 flex items-center gap-1">
                  {summary.totalDownvotes} downvotes <ThumbsDown className="w-3.5 h-3.5" />
                </span>
              </div>
              <div className="h-4 rounded-full overflow-hidden flex bg-gray-200 dark:bg-gray-700">
                <div
                  className="bg-green-500 dark:bg-green-400 transition-all"
                  style={{ width: `${upPct}%` }}
                />
                <div
                  className="bg-red-500 dark:bg-red-400 transition-all"
                  style={{ width: `${100 - upPct}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1 text-center">
                {upPct}% positive
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-white dark:bg-gray-900 rounded-lg p-3">
                <p className="text-gray-500 dark:text-gray-400">Avg Requests / Attendee</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">{summary.avgRequestsPerAttendee}</p>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded-lg p-3">
                <p className="text-gray-500 dark:text-gray-400">Avg Votes / Voter</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">{summary.avgVotesPerAttendee}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Engagement Funnel */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary" />
          Engagement Funnel
        </h3>
        {summary.uniqueAttendees === 0 ? (
          <div className="h-32 flex items-center justify-center text-gray-500">
            <p>No participant data yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {[
              { label: 'Total Participants', count: summary.uniqueAttendees, icon: Users, color: 'bg-blue-500' },
              { label: 'Submitted Requests', count: summary.uniqueRequesters, icon: Send, color: 'bg-indigo-500' },
              { label: 'Cast Votes', count: summary.uniqueVoters, icon: ThumbsUp, color: 'bg-purple-500' },
              { label: 'Ranked Songs', count: summary.uniqueRankers, icon: Star, color: 'bg-amber-500' },
            ].map((step) => {
              const pct = summary.uniqueAttendees > 0
                ? Math.round((step.count / summary.uniqueAttendees) * 100)
                : 0;
              return (
                <div key={step.label}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                      <step.icon className="w-3.5 h-3.5" /> {step.label}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 tabular-nums">{step.count} ({pct}%)</span>
                  </div>
                  <div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${step.color} rounded-full transition-all`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Voting Velocity Chart */}
      {velocityData.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6 md:col-span-2">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Voting Velocity
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={velocityData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="time" tick={{ fontSize: 11, fill: '#9CA3AF' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#F3F4F6',
                  fontSize: 12,
                }}
              />
              <Line type="monotone" dataKey="upvotes" stroke="#10B981" strokeWidth={2} name="Upvotes" dot={false} />
              <Line type="monotone" dataKey="downvotes" stroke="#EF4444" strokeWidth={2} name="Downvotes" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
